import { useEffect } from "react"
import { useLocation, useMatches } from "react-router"
import { postToNative } from "../../lib/nativeBridge"

/**
 * Emits a `navigation-change` event to the native Expo host on every route
 * change (and on initial mount), so the native shell can listen for where the
 * user is in the web app. This is prep for replacing specific web views with
 * native screens (starting with the CHM submission view).
 *
 * The payload is intentionally generic — `pathname`, `search`, and the merged
 * route `params` (e.g. `{ incidentId, id }`). The native side reconstructs
 * meaning from those. `postToNative` no-ops outside the Expo WebView, so this
 * hook is safe to run in a plain browser.
 *
 * Mount this exactly once, inside the router (in `Layout`), which is the only
 * place that both has react-router context and persists across all navigations.
 */
export function useNavigationListener() {
  const location = useLocation()
  const matches = useMatches()

  useEffect(() => {
    // Merge params across the matched route branch so nested segments (leaf
    // wins) are all present, e.g. { incidentId, id } for the submit route.
    const params = matches.reduce<Record<string, string | undefined>>(
      (acc, match) => ({ ...acc, ...match.params }),
      {},
    )

    postToNative('navigation-change', {
      pathname: location.pathname,
      search: location.search,
      params,
    })
    // `matches` is intentionally excluded from the deps: React Router returns a
    // fresh `matches` array reference on every render, so listing it would fire
    // this effect on unrelated re-renders. pathname/search fully capture route
    // changes, and `matches` only ever changes in lockstep with them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])
}
